<script setup lang="ts">
import type { IconProps } from '/#/components/base/icon/icon'

const props = withDefaults(defineProps<IconProps>(), {
  size: 'sm',
  color: 'slate',
  shade: undefined,
  collection: 'custom',
  variants: undefined,
})

const simpleColors = ['white', 'black', 'inherit']

/**
 * @method isSimpleColor
 * @description Checks if a given color is one of the "simple" colors (white, black, inherit).
 * @returns {boolean} True if the color is a simple color, otherwise false.
 */
function isSimpleColor(color?: string): boolean {
  if (!color) return false
  return simpleColors.includes(color)
}

/**
 * @name iconName
 * @description Computes the icon name based on the collection, name and variants props.
 * For flag collection, it includes the variant in the icon name (e.g. 'flag:us-square')
 * For other collections, it just combines collection and name (e.g. 'custom:home')
 * @returns {string} The formatted icon name string to be used by IconifyIcon component
 */
const iconName = computed(() => {
  if (props.collection === 'flag') {
    return `flag:${props.name}-${props.variants}`
  }

  return `${props.collection}:${props.name}`
})

/**
 * @name colorClass
 * @description Computes the Tailwind CSS color class based on color and shades props.
 * - Returns undefined if no color is provided
 * - For white/black colors, returns simple color class (e.g. 'text-white')
 * - For slate color, uses shade 800
 * - For other colors, uses shade 500 by default
 * @returns {string|undefined} The Tailwind CSS color class or undefined
 */
const colorClass = computed(() => {
  if (!props.color) return undefined

  // For white/black colors, return simple color class
  if (isSimpleColor(props.color)) {
    return `text-${props.color}`
  }

  if (props.color === 'critical') {
    return 'text-primary-800'
  }

  // For slate color use shade 800, for others use 500
  const shade = props.color === 'slate' ? 800 : 500
  return `text-${props.color}-${props.shade || shade}`
})

/**
 * @name backgroundColorClass
 * @description Computes the Tailwind CSS background color class based on `backgroundColor` and `shade` props.
 * - Returns undefined if no `backgroundColor` is provided
 * - For white/black/inherit colors, returns a simple background class (e.g. `bg-white`)
 * @returns {string|undefined} The Tailwind CSS background color class or undefined
 */
const backgroundColorClass = computed(() => {
  if (!props.backgroundColor) return undefined

  // For white/black colors, return simple color class
  if (isSimpleColor(props.backgroundColor)) {
    return `has-bg bg-${props.backgroundColor}`
  }

  return `has-bg bg-${props.backgroundColor}-50`
})
</script>

<script lang="ts">
export default {
  inheritAttrs: false,
}
</script>

<template>
  <div :class="[backgroundColorClass]" class="text-inherit">
    <IconifyIcon
      v-tippy="tippy"
      v-bind="$attrs"
      :icon="iconName"
      :class="[size, colorClass]"
      class="app-icon"
     
    />
  </div>
</template>
